import { useState, useMemo } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Search, CheckSquare, Video } from 'lucide-react'
import { format } from 'date-fns'
import { es } from 'date-fns/locale'
import { VencimientoCard } from './VencimientoCard'
import type { VencimientoConCliente } from '@/types/vencimientos'
import type { TareaCompleta } from '@/types/database'
import type { EventoCalendario, TareaProyectada } from '@/core/types/calendario'
import { horaVisibleEvento } from '@/lib/fechaEvento'

interface VencimientosDiaDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  fecha: Date | null
  vencimientos: VencimientoConCliente[]
  onMarcarPresentado: (vencimiento: VencimientoConCliente) => void
  onVerDetalles: (vencimiento: VencimientoConCliente) => void
  /** ORR-228: el día es de los tres tipos, no solo de vencimientos */
  tareas?: TareaCompleta[]
  eventos?: EventoCalendario[]
  onVerTarea?: (tarea: TareaCompleta) => void
  onVerEvento?: (evento: EventoCalendario) => void
  /**
   * ORR-331: ocurrencias de recurrentes que todavía no son filas. Van acá y no en `tareas` porque
   * no se pueden abrir ni operar — sin ellas, un día que solo tiene programadas abriría el diálogo
   * vacío, que es el mismo agujero que cerró ORR-228.
   */
  proyectadas?: TareaProyectada[]
}

/** Identidad estable para las props opcionales: un `[]` inline rompe la memoización. */
const SIN_TAREAS: TareaCompleta[] = []
const SIN_EVENTOS: EventoCalendario[] = []
const SIN_PROYECTADAS: TareaProyectada[] = []

export function VencimientosDiaDialog({
  open,
  onOpenChange,
  fecha,
  vencimientos,
  onMarcarPresentado,
  onVerDetalles,
  tareas = SIN_TAREAS,
  eventos = SIN_EVENTOS,
  onVerTarea,
  onVerEvento,
  proyectadas = SIN_PROYECTADAS,
}: VencimientosDiaDialogProps) {
  const [busqueda, setBusqueda] = useState('')
  const [filtroImpuesto, setFiltroImpuesto] = useState<string | null>(null)

  // Get unique tax types
  const tiposImpuesto = useMemo(() => {
    const tipos = new Set(vencimientos.map((v) => v.tipo_impuesto ?? ''))
    return Array.from(tipos).filter(Boolean).sort()
  }, [vencimientos])

  // Filter vencimientos
  const vencimientosFiltrados = useMemo(() => {
    return vencimientos.filter((v) => {
      if (busqueda) {
        const term = busqueda.toLowerCase()
        const matchCliente = (v.cliente_nombre || '').toLowerCase().includes(term)
        const matchTipo = (v.tipo_impuesto ?? '').toLowerCase().includes(term)
        if (!matchCliente && !matchTipo) return false
      }
      if (filtroImpuesto && (v.tipo_impuesto ?? '') !== filtroImpuesto) return false
      return true
    })
  }, [vencimientos, busqueda, filtroImpuesto])

  // Con un impuesto elegido, tareas y eventos se ocultan: no tienen impuesto que matchear,
  // y dejarlos visibles haría parecer que el filtro no se aplicó.
  const tareasFiltradas = useMemo(() => {
    if (filtroImpuesto) return SIN_TAREAS
    if (!busqueda) return tareas
    const term = busqueda.toLowerCase()
    return tareas.filter(
      (t) =>
        (t.titulo || '').toLowerCase().includes(term) ||
        (t.cliente_nombre || '').toLowerCase().includes(term),
    )
  }, [tareas, busqueda, filtroImpuesto])

  const eventosFiltrados = useMemo(() => {
    if (filtroImpuesto) return SIN_EVENTOS
    if (!busqueda) return eventos
    const term = busqueda.toLowerCase()
    return eventos.filter((e) => (e.titulo || '').toLowerCase().includes(term))
  }, [eventos, busqueda, filtroImpuesto])

  const proyectadasFiltradas = useMemo(() => {
    if (filtroImpuesto) return SIN_PROYECTADAS
    if (!busqueda) return proyectadas
    const term = busqueda.toLowerCase()
    return proyectadas.filter((p) => (p.titulo || '').toLowerCase().includes(term))
  }, [proyectadas, busqueda, filtroImpuesto])

  const totalMostrado =
    vencimientosFiltrados.length + tareasFiltradas.length + eventosFiltrados.length + proyectadasFiltradas.length
  const totalDelDia = vencimientos.length + tareas.length + eventos.length + proyectadas.length

  const handleClose = (value: boolean) => {
    if (!value) {
      setBusqueda('')
      setFiltroImpuesto(null)
    }
    onOpenChange(value)
  }

  if (!fecha) return null

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px] h-[550px] max-h-[85vh] flex flex-col p-0">
        {/* Header */}
        <div className="px-6 pt-6 pb-0">
          <DialogHeader>
            <DialogTitle className="capitalize">
              Agenda del {format(fecha, "d 'de' MMMM", { locale: es })}
            </DialogTitle>
            <DialogDescription>
              Mostrando {totalMostrado} de {totalDelDia} ítem{totalDelDia !== 1 ? 's' : ''}
            </DialogDescription>
          </DialogHeader>
        </div>

        {/* Sticky zone: search + filter chips */}
        <div className="px-6 py-3 border-b space-y-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por cliente, impuesto o tarea..."
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              className="pl-9 h-9"
            />
          </div>

          {tiposImpuesto.length > 1 && (
            <div className="flex flex-wrap gap-1.5">
              {tiposImpuesto.map((tipo) => (
                <Badge
                  key={tipo}
                  variant={filtroImpuesto === tipo ? 'default' : 'outline'}
                  className="cursor-pointer text-xs"
                  onClick={() =>
                    setFiltroImpuesto((prev) => (prev === tipo ? null : tipo))
                  }
                >
                  {tipo}
                </Badge>
              ))}
            </div>
          )}
        </div>

        {/* Scrollable area */}
        <div className="flex-1 overflow-y-auto px-6 py-3">
          <div className="space-y-4">
            {vencimientosFiltrados.length > 0 && (
              <div className="space-y-2">
                {vencimientosFiltrados.map((v) => (
                  <VencimientoCard
                    key={v.id}
                    vencimiento={v}
                    compact
                    onMarcarPresentado={(venc) => {
                      handleClose(false)
                      onMarcarPresentado(venc)
                    }}
                    onVerDetalles={(venc) => {
                      handleClose(false)
                      onVerDetalles(venc)
                    }}
                  />
                ))}
              </div>
            )}

            {tareasFiltradas.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                  Tareas
                </p>
                {tareasFiltradas.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    className="w-full text-left rounded-lg border p-3 hover:bg-muted/50 transition-colors"
                    onClick={() => {
                      handleClose(false)
                      onVerTarea?.(t)
                    }}
                  >
                    <span className="flex items-start gap-2">
                      <CheckSquare className="h-4 w-4 shrink-0 mt-0.5 text-emerald-600" />
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-medium truncate">{t.titulo}</span>
                        {t.cliente_nombre && (
                          <span className="block text-xs text-muted-foreground truncate">
                            {t.cliente_nombre}
                          </span>
                        )}
                      </span>
                      {t.estado && (
                        <Badge variant="outline" className="text-xs shrink-0">
                          {t.estado}
                        </Badge>
                      )}
                    </span>
                  </button>
                ))}
              </div>
            )}

            {proyectadasFiltradas.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                  Programadas
                </p>
                {/* Un div, no un button: no hay nada que abrir todavía. */}
                {proyectadasFiltradas.map((p) => (
                  <div
                    key={`${p.tarea_recurrente_id}:${p.fecha}`}
                    className="w-full rounded-lg border border-dashed p-3 text-muted-foreground"
                  >
                    <span className="flex items-start gap-2">
                      <CheckSquare className="h-4 w-4 shrink-0 mt-0.5" />
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-medium truncate">{p.titulo}</span>
                        <span className="block text-xs">Se crea sola ese día</span>
                      </span>
                    </span>
                  </div>
                ))}
              </div>
            )}

            {eventosFiltrados.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                  Reuniones
                </p>
                {eventosFiltrados.map((e) => (
                  <button
                    key={e.id}
                    type="button"
                    className="w-full text-left rounded-lg border p-3 hover:bg-muted/50 transition-colors"
                    onClick={() => {
                      handleClose(false)
                      onVerEvento?.(e)
                    }}
                  >
                    <span className="flex items-start gap-2">
                      <Video className="h-4 w-4 shrink-0 mt-0.5 text-blue-600" />
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-medium truncate">{e.titulo}</span>
                        <span className="block text-xs text-muted-foreground">
                          {horaVisibleEvento(e) ?? 'Todo el día'}
                        </span>
                      </span>
                    </span>
                  </button>
                ))}
              </div>
            )}

            {totalMostrado === 0 && (
              <p className="text-sm text-muted-foreground text-center py-8">
                {totalDelDia === 0
                  ? 'No hay nada agendado para este día'
                  : 'No se encontró nada con los filtros aplicados'}
              </p>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
